import { IPaginatedResponse } from '../../types/paginated-response.type'
import { IError } from '../base/error'
import ServiceBase from '../base/service.base'
import { CompanySegment } from '../common/company.enum'

import {
  ICompany,
  ICompanyCreateRequest,
  ICompanyProfileAsssetsDTO,
  ICompanyProfileDocumentsCreate,
  ICompanyProvidersResponse,
  CompanySegmentType,
  ICompanySegmentAlias,
  ICompanyProvidersResponseDTO,
  IInternalDocument,
  ICompanyProfileDocuments,
  ICompanyResponse,
  ICompanyAddress,
  ISocialEnvironmentalRisk,
  ICompanyRanking,
  IProviderDashboard,
  GetProvidersAlltiersMap,
  IRankedProviderDTO,
} from './company.types'

export class Company extends ServiceBase {
  public async createCompany(data: ICompanyCreateRequest): Promise<ICompany> {
    const url = `/company`

    const response = await this.request.post<ICompany, IError>(url, data)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async createCompanyProviderForUser(taxId: string): Promise<boolean> {
    const url = `/company/providers`

    const response = await this.request.post<boolean, IError>(url, {
      taxId,
    })

    if (response.status === 201) {
      return true
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getProvidersPendingInvoices(): Promise<IInternalDocument[]> {
    const url = '/company/documents/internal'

    const response = await this.request.get<IInternalDocument[], IError>(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getProvidersSegmentsInconsistencies(): Promise<string> {
    const url = '/company/segments/inconsistencies'

    const response = await this.request.get<string, IError>(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getProvidersAllTiersList(
    page?: number,
    search?: string,
    status?: string,
    segment?: string,
    existSocialEnvironmentalRisk?: boolean,
    limit?: number,
  ): Promise<IPaginatedResponse<ICompanyProvidersResponseDTO[]>> {
    const url = `/company/providers/alltiers/list`

    const response = await this.request.get<
      IPaginatedResponse<ICompanyProvidersResponseDTO[]>,
      IError
    >(url, {
      params: {
        page: page ?? 1,
        search,
        status,
        segment,
        existSocialEnvironmentalRisk,
        limit: limit ?? 50,
      },
    })

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getProvidersAllTiersProgressive(
    rootId?: string,
    companiesIds?: string[],
    direction: 'next' | 'previous' = 'next',
  ): Promise<ICompanyProvidersResponseDTO[]> {
    let url = `/company/providers/alltiers/progressive`

    url += `?isProvidersExpand=${direction === 'next'}`

    if (rootId) {
      url += `&companyToExpand=${rootId}`
    }
    if (companiesIds?.length) {
      url += `&companiesIds=${companiesIds.join(',')}`
    }

    const response = await this.request.get<
      ICompanyProvidersResponseDTO[],
      IError
    >(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getFirsttierProviders(): Promise<
    IPaginatedResponse<ICompanyProvidersResponse[]>
  > {
    const url = '/company/providers/firsttier'

    const response = await this.request.get<
      IPaginatedResponse<ICompanyProvidersResponse[]>,
      IError
    >(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getFirsttierProvidersByTaxId(
    taxId: string,
    page?: number,
    limit?: number,
  ): Promise<IPaginatedResponse<ICompanyProvidersResponseDTO[]>> {
    const url = `company/providers/firsttier/providers/${taxId}`

    const response = await this.request.get<
      IPaginatedResponse<ICompanyProvidersResponseDTO[]>,
      IError
    >(url, {
      params: {
        page: page ?? 1,
        limit: limit ?? 5,
      },
    })

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getFirsttierClientsByTaxId(
    taxId: string,
    page?: number,
    limit?: number,
  ): Promise<IPaginatedResponse<ICompanyProvidersResponseDTO[]>> {
    const url = `company/providers/firsttier/clients/${taxId}`

    const response = await this.request.get<
      IPaginatedResponse<ICompanyProvidersResponseDTO[]>,
      IError
    >(url, {
      params: {
        page: page ?? 1,
        limit: limit ?? 5,
      },
    })

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getBranched(): Promise<ICompanyProvidersResponse[]> {
    const url = '/company/branches'

    const response = await this.request.get<
      ICompanyProvidersResponse[],
      IError
    >(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getAssets(
    userEmail: string,
  ): Promise<ICompanyProfileAsssetsDTO> {
    const url = `/company/profile/assets/user/${userEmail}`

    const response = await this.request.get<ICompanyProfileAsssetsDTO, IError>(
      url,
    )

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getSegmentsAliases(): Promise<CompanySegmentType> {
    const url = `/company/profile/aliases/segments`

    const response = await this.request.get<CompanySegmentType, IError>(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async updateAssets(logo: File): Promise<boolean> {
    const url = `/company/profile/assets`

    const data = new FormData()
    data.append('logo', logo, logo.name)

    const response = await this.request.post<boolean, IError>(url, data)

    if (response.status === 200) {
      return true
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async updateDocuments(
    document: ICompanyProfileDocumentsCreate,
  ): Promise<boolean> {
    const url = `company/documents`
    const data = new FormData()
    data.append('file', document.file, document.file.name)
    data.append('name', document.name)
    data.append('category', `${document.category}`)
    document.expiration && data.append('expiration', document.expiration)

    const response = await this.request.post<boolean, IError>(url, data)

    if (response.status === 201) {
      return true
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getDocuments(id: string): Promise<ICompanyProfileDocuments[]> {
    const url = `/company/documents/external?id=${id}`

    const response = await this.request.get<ICompanyProfileDocuments[], IError>(
      url,
    )
    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getInternalDocuments(): Promise<ICompanyProfileDocuments[]> {
    const url = `/company/documents/internal`

    const response = await this.request.get<ICompanyProfileDocuments[], IError>(
      url,
    )
    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getExpiringDocuments(): Promise<ICompanyProfileDocuments[]> {
    const url = `/company/documents/expiring`

    const response = await this.request.get<ICompanyProfileDocuments[], IError>(
      url,
    )
    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getExpiredDocuments(): Promise<ICompanyProfileDocuments[]> {
    const url = `/company/documents/expired`

    const response = await this.request.get<ICompanyProfileDocuments[], IError>(
      url,
    )
    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async deleteDocument(id: string): Promise<boolean> {
    const url = `/company/documents/${id}`

    const response = await this.request.delete<boolean, IError>(url)

    if (response.status === 200) {
      return true
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  /**
   * Get specific company data by tax id
   *
   * @param taxId company tax id
   * @returns company
   */
  public async getCompany(taxId: string): Promise<ICompanyResponse> {
    const url = `/company/${taxId}`

    const response = await this.request.get<ICompanyResponse, IError>(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  /**
   * Get specific company data by tax id
   *
   * @param taxId company tax id
   * @returns company
   */
  public async getCompanyById(id: string): Promise<ICompanyResponse> {
    const url = `/company/id/${id}`

    const response = await this.request.get<ICompanyResponse, IError>(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  /**
   * Get company segments by logged user
   *
   * @returns segments
   */
  public async getSegments(
    companyId?: string,
  ): Promise<ICompanySegmentAlias[]> {
    const url = `/company/segments`

    const response = await this.request.get<ICompanySegmentAlias[], IError>(
      url,
      {
        params: {
          companyId,
        },
      },
    )

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  /**
   * Get all segments
   *
   * @returns segments
   */
  public async getAllSegments(): Promise<ICompanySegmentAlias[]> {
    const url = `/company/segments/all`

    const response = await this.request.get<ICompanySegmentAlias[], IError>(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  /**
   * Get social environmental risk
   *
   * @returns social environmental risk
   */
  public async getSocialEnvironmentalRisk(
    taxId: string,
  ): Promise<IPaginatedResponse<ISocialEnvironmentalRisk[]>> {
    const url = `/social-environmental-risk/company/${taxId}`

    const response = await this.request.get<
      IPaginatedResponse<ISocialEnvironmentalRisk[]>,
      IError
    >(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async updateRiskChecked(
    riskId: string,
    checked: boolean,
  ): Promise<boolean> {
    const url = `/social-environmental-risk/checked`

    const response = await this.request.patch<void, IError>(url, {
      id: riskId,
      checked,
    })

    if (response.status === 200) {
      return true
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  /**
   * Change company Segment
   *
   * @param segment segment to be changed
   * @returns company
   */
  public async changeSegment(
    segment: CompanySegment,
    email: string,
  ): Promise<void> {
    const url = `/company`

    await this.request.patch<ICompany, IError>(url, {
      segment,
      email,
    })
  }

  /**
   * Change company Address
   *
   * @param address address to be changed
   * @returns company
   */
  public async changeAddress(
    address: ICompanyAddress,
    email: string,
  ): Promise<void> {
    const url = `/company`

    await this.request.patch<ICompany, IError>(url, {
      address,
      email,
    })
  }

  /**
   * Change company Status
   *
   * @param status status to be changed
   * @returns company
   */
  public async changeStatus(companyId: string, status: string): Promise<void> {
    const url = `/company/provider/${companyId}/status`

    await this.request.patch<ICompany, IError>(url, {
      status,
    })
  }

  public async getFirsttierProvidersRanking(
    page: number,
  ): Promise<IPaginatedResponse<ICompanyRanking[]>> {
    const url = `/company/providers/firsttiers/ranking`

    const response = await this.request.get<
      IPaginatedResponse<ICompanyRanking[]>,
      IError
    >(url, {
      params: {
        page,
        isClient: false,
        limit: 5,
      },
    })

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getFirsttierClientsRanking(
    page: number,
  ): Promise<IPaginatedResponse<ICompanyRanking[]>> {
    const url = `/company/providers/firsttiers/ranking`

    const response = await this.request.get<
      IPaginatedResponse<ICompanyRanking[]>,
      IError
    >(url, {
      params: {
        page,
        isClient: true,
        limit: 5,
      },
    })

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getFirsttierWithRisk(
    page: number,
  ): Promise<IPaginatedResponse<ICompanyProvidersResponseDTO[]>> {
    const url = `/company/providers/firsttier`

    const response = await this.request.get<
      IPaginatedResponse<ICompanyProvidersResponseDTO[]>,
      IError
    >(url, {
      params: {
        page,
        limit: 5,
        existSocialEnvironmentalRisk: true,
      },
    })
    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getDashboardData(): Promise<IProviderDashboard> {
    const url = `/company/providers/dashboard`

    const response = await this.request.get<IProviderDashboard, IError>(url)
    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getProvidersMap(
    zoom: number,
    northEastLat: number,
    northEastLng: number,
    southWestLat: number,
    southWestLng: number,
    companyId?: string,
  ) {
    const url = `/company/providers/alltiers/map`

    const response = await this.request.get<GetProvidersAlltiersMap[], IError>(
      url,
      {
        params: {
          zoom,
          northEastLat,
          northEastLng,
          southWestLat,
          southWestLng,
          companyId: companyId || undefined,
        },
      },
    )
    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getRankedProviders(
    page?: number,
    search?: string,
    status?: string,
    segment?: string,
    existSocialEnvironmentalRisk?: boolean,
    integrationStatus?: string,
    certificateType?: string,
    limit?: number,
  ): Promise<IPaginatedResponse<IRankedProviderDTO[]>> {
    const url = `/company/providers/ranking`

    const response = await this.request.get<
      IPaginatedResponse<IRankedProviderDTO[]>,
      IError
    >(url, {
      params: {
        page: page ?? 1,
        search,
        status,
        segment,
        existSocialEnvironmentalRisk: existSocialEnvironmentalRisk || undefined,
        integrationStatus,
        certificateType: certificateType ?? undefined,
        limit: limit ?? 5,
      },
    })

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }
}
